import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  pdtaData, examTypeStatuses, statusLabels, describeFrequency, weekOrdinal, weekdayShort
} from "@/data/adminConfigData";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import {
  Sparkles, Plus, Pencil, Trash2, Copy, Repeat, Clock, ListChecks
} from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  testing: "bg-amber-50 text-amber-700 border-amber-200",
  attivo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  non_attivo: "bg-rose-50 text-rose-700 border-rose-200"
};

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "nome" },
  { key: "description", label: "descrizione" },
  { key: "status", label: "stato" },
  { key: "stepsJson", label: "steps_json", get: (r) => JSON.stringify(r.steps), parse: (v) => { try { return JSON.parse(v); } catch { return []; } } }
];

const newStep = () => ({
  id: `s${Date.now()}`,
  serviceId: servicesData[0]?.id || null,
  bundleId: null,
  startOffset: { value: 0, unit: "days" },
  frequency: { kind: "weekly", every: 1, dayOfWeek: 1 },
  repetitions: 4,
  durationDays: 28
});

const empty = { name: "", description: "", status: "draft", steps: [], createdAt: new Date().toISOString().slice(0, 10) };

const offsetUnits = [
  { id: "days", label: "giorni" }, { id: "weeks", label: "settimane" }, { id: "months", label: "mesi" }
];

const StepEditor = ({ step, onChange, onRemove }) => {
  const f = step.frequency;
  const setF = (patch) => onChange({ ...step, frequency: { ...f, ...patch } });
  const isService = !!step.serviceId;

  return (
    <div className="p-3 rounded-lg border bg-card space-y-3" data-testid={`pdta-step-${step.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Step</Badge>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Service or bundle */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Tipo</Label>
          <select className="w-full mt-1 border rounded-md p-1.5 text-sm bg-background"
            value={isService ? "service" : "bundle"}
            onChange={(e) => onChange({ ...step,
              serviceId: e.target.value === "service" ? servicesData[0].id : null,
              bundleId: e.target.value === "bundle" ? serviceBundlesData[0].id : null
            })}>
            <option value="service">Singola prestazione</option>
            <option value="bundle">Bundle (profilo)</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">{isService ? "Prestazione" : "Bundle"}</Label>
          <select className="w-full mt-1 border rounded-md p-1.5 text-sm bg-background"
            value={isService ? step.serviceId : step.bundleId}
            onChange={(e) => isService ? onChange({ ...step, serviceId: e.target.value }) : onChange({ ...step, bundleId: e.target.value })}>
            {isService
              ? servicesData.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
              : serviceBundlesData.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {/* Start offset */}
      <div>
        <Label className="text-xs">Inizio da (offset dall'attivazione)</Label>
        <div className="flex items-center gap-2">
          <Input type="number" min="0" className="w-20 h-8" value={step.startOffset.value}
            onChange={(e) => onChange({ ...step, startOffset: { ...step.startOffset, value: Number(e.target.value) } })} />
          <select className="flex-1 border rounded-md p-1.5 text-sm bg-background"
            value={step.startOffset.unit}
            onChange={(e) => onChange({ ...step, startOffset: { ...step.startOffset, unit: e.target.value } })}>
            {offsetUnits.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
        </div>
      </div>

      {/* Frequency */}
      <div className="space-y-2 p-2 rounded-md bg-muted/30">
        <div className="flex items-center gap-2">
          <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
          <Label className="text-xs">Ricorrenza</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select className="border rounded-md p-1.5 text-sm bg-background"
            value={f.kind}
            onChange={(e) => {
              const kind = e.target.value;
              const def = { once: { kind, every: 1 }, daily: { kind, every: 1 }, weekly: { kind, every: 1, dayOfWeek: 1 }, monthly: { kind, every: 1, dayOfMonth: 1 } };
              onChange({ ...step, frequency: def[kind] });
            }}>
            <option value="once">Una sola volta</option>
            <option value="daily">Giornaliera</option>
            <option value="weekly">Settimanale</option>
            <option value="monthly">Mensile</option>
          </select>
          {f.kind !== "once" && (
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-muted-foreground text-xs">Ogni</span>
              <Input type="number" min="1" className="w-16 h-7" value={f.every}
                onChange={(e) => setF({ every: Number(e.target.value) })} />
              <span className="text-muted-foreground text-xs">
                {f.kind === "daily" && (f.every === 1 ? "giorno" : "giorni")}
                {f.kind === "weekly" && (f.every === 1 ? "settimana" : "settimane")}
                {f.kind === "monthly" && (f.every === 1 ? "mese" : "mesi")}
              </span>
            </div>
          )}
        </div>

        {f.kind === "weekly" && (
          <div>
            <Label className="text-xs">Giorno</Label>
            <select className="w-full mt-1 border rounded-md p-1.5 text-sm bg-background"
              value={f.dayOfWeek || 1}
              onChange={(e) => setF({ dayOfWeek: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5, 6, 7].map(d => <option key={d} value={d}>{["", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"][d]}</option>)}
            </select>
          </div>
        )}

        {f.kind === "monthly" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input type="radio" id={`m-day-${step.id}`} checked={!!f.dayOfMonth} onChange={() => setF({ dayOfMonth: 1, weekOfMonth: undefined })} />
              <Label htmlFor={`m-day-${step.id}`} className="text-xs cursor-pointer">Giorno fisso</Label>
              {f.dayOfMonth !== undefined && (
                <Input type="number" min="1" max="31" className="w-16 h-7 ml-2" value={f.dayOfMonth}
                  onChange={(e) => setF({ dayOfMonth: Number(e.target.value) })} />
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="radio" id={`m-week-${step.id}`} checked={!!f.weekOfMonth} onChange={() => setF({ weekOfMonth: 1, dayOfWeek: 1, dayOfMonth: undefined })} />
              <Label htmlFor={`m-week-${step.id}`} className="text-xs cursor-pointer">N° giorno settimana</Label>
              {f.weekOfMonth !== undefined && (
                <>
                  <select className="border rounded-md p-1 text-xs bg-background ml-1"
                    value={f.weekOfMonth}
                    onChange={(e) => setF({ weekOfMonth: Number(e.target.value) })}>
                    {[1, 2, 3, 4].map(w => <option key={w} value={w}>{weekOrdinal[w]}</option>)}
                  </select>
                  <select className="border rounded-md p-1 text-xs bg-background"
                    value={f.dayOfWeek || 1}
                    onChange={(e) => setF({ dayOfWeek: Number(e.target.value) })}>
                    {[1, 2, 3, 4, 5, 6, 7].map(d => <option key={d} value={d}>{weekdayShort[d]}</option>)}
                  </select>
                </>
              )}
            </div>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground italic">{describeFrequency(f)}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">N° ripetizioni</Label>
          <Input type="number" min="1" value={step.repetitions} onChange={(e) => onChange({ ...step, repetitions: Number(e.target.value) })} className="h-8" />
        </div>
        <div>
          <Label className="text-xs">Durata totale (giorni)</Label>
          <Input type="number" min="0" value={step.durationDays} onChange={(e) => onChange({ ...step, durationDays: Number(e.target.value) })} className="h-8" />
        </div>
      </div>
    </div>
  );
};

export const AdminPDTAPage = () => {
  const [items, setItems] = useState(pdtaData);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (it) => {
    setEditing(it
      ? { ...it, steps: it.steps.map(s => ({ ...s, startOffset: { ...s.startOffset }, frequency: { ...s.frequency } })) }
      : { ...empty, id: `pdta${Date.now()}`, steps: [newStep()] });
    setOpen(true);
  };

  const clone = (it) => {
    setItems(prev => [...prev, {
      ...it,
      id: `${it.id}_copy${Date.now().toString().slice(-4)}`,
      name: `${it.name} (copia)`,
      status: "draft",
      steps: it.steps.map(s => ({ ...s, id: `s${Math.random().toString(36).slice(2)}`, startOffset: { ...s.startOffset }, frequency: { ...s.frequency } }))
    }]);
    toast.success("PDTA clonato");
  };

  const save = () => {
    if (!editing.name) { toast.error("Inserire nome PDTA"); return; }
    if (editing.steps.length === 0) { toast.error("Aggiungere almeno uno step"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("PDTA salvato");
  };

  const remove = (id) => {
    setItems(prev => prev.filter(p => p.id !== id));
    toast.success("PDTA rimosso");
  };

  const handleImport = (parsed) => {
    setItems(parsed.map(p => ({ ...empty, ...p, steps: Array.isArray(p.stepsJson) ? p.stepsJson : (p.steps || []) })));
  };

  const updateStep = (idx, next) => setEditing(e => ({ ...e, steps: e.steps.map((s, i) => i === idx ? next : s) }));
  const removeStep = (idx) => setEditing(e => ({ ...e, steps: e.steps.filter((_, i) => i !== idx) }));
  const addStep = () => setEditing(e => ({ ...e, steps: [...e.steps, { ...newStep(), id: `s${Date.now()}_${e.steps.length}` }] }));

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">6.4 Configurazione</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">PDTA · Percorsi Diagnostico Terapeutici</h1>
          <p className="text-sm text-muted-foreground mt-1">Workflow temporali multi-step attivabili da medico o farmacista</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="pdta" onImport={handleImport} testIdPrefix="pdta-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="pdta-new-btn">
            <Plus className="h-4 w-4" /> Nuovo PDTA
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(p => (
          <Card key={p.id} data-testid={`pdta-card-${p.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{p.name}</CardTitle>
                <Badge variant="outline" className={STATUS_STYLE[p.status]}>{statusLabels[p.status]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="gap-1"><ListChecks className="h-3 w-3" />{p.steps.length} step</Badge>
                <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Creato {p.createdAt}</Badge>
              </div>
              <div className="space-y-1.5 pt-2 border-t">
                {p.steps.slice(0, 3).map((s, i) => {
                  const ref = s.serviceId ? servicesData.find(x => x.id === s.serviceId)?.name : serviceBundlesData.find(x => x.id === s.bundleId)?.name;
                  return (
                    <div key={s.id} className="text-xs flex items-start gap-2">
                      <Badge variant="outline" className="shrink-0 h-5 px-1.5 text-[10px]">{i + 1}</Badge>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{ref || "—"}</p>
                        <p className="text-muted-foreground text-[11px]">{describeFrequency(s.frequency)} · {s.repetitions}× </p>
                      </div>
                    </div>
                  );
                })}
                {p.steps.length > 3 && <p className="text-[11px] text-muted-foreground italic">+{p.steps.length - 3} altri step</p>}
              </div>
              <div className="flex justify-end gap-1 pt-2 border-t">
                <Button variant="ghost" size="icon" onClick={() => clone(p)} title="Clona" data-testid={`pdta-clone-${p.id}`}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => startEdit(p)} data-testid={`pdta-edit-${p.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(p.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica PDTA" : "Nuovo PDTA"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="pdta-form-name" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label>Stato</Label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {examTypeStatuses.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Steps ({editing.steps.length})</Label>
                  <Button variant="outline" size="sm" onClick={addStep} className="gap-1" data-testid="pdta-add-step-btn">
                    <Plus className="h-3 w-3" /> Aggiungi step
                  </Button>
                </div>
                <div className="space-y-3">
                  {editing.steps.map((s, idx) => (
                    <StepEditor key={s.id} step={s} onChange={(next) => updateStep(idx, next)} onRemove={() => removeStep(idx)} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="pdta-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPDTAPage;
